import Button from '@/component/Button'
import Form, { Field, useForm } from '@/component/FieldForm'
import Input from '@/component/Input'
import Loading from '@/component/Loading'
import TGASDK from '@/config/TGASDK'
import useChargeCommodityList from '@/hook/data/useChargeCommodityList'
import { PopUpStore } from '@/store/PopupStore'
import type { ChargeCommodity } from '@kulee/tga-sdk/types/types/wallet'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DiamondCommodityItem from '../DiamondCommodityItem'

const DiamondForm: React.FC<PropType> = () => {
  const { t } = useTranslation('wallet')
  const [form] = useForm<FormValuesType>()
  const { data: commodities } = useChargeCommodityList('DIAMOND')
  const [commodity, setCommodity] = useState<ChargeCommodity | null>(null)
  const [quantity, setQuantity] = useState<number>(1)

  const MIN_TOTAL = 1
  const MAX_TOTAL = 3000

  // 查找数量为 1 的商品，用于自定义购买
  const baseOneCommodity = commodities?.find(c => c.quantity === 1) || null
  const otherCommodities = commodities?.filter(c => c.quantity !== 1) || []

  // 选择商品
  const handleSelect = (item: ChargeCommodity) => {
    setCommodity(item)
    setQuantity(1)
    form.setFieldsValue({
      commodityId: item.id,
      total: item.quantity + item.freeQuantity
    })
  }

  // 监听 total 输入变化，自动匹配商品
  const handleTotalChange = (value?: number) => {
    if (!commodities) return

    // 如果 value 不是数字，直接 return
    if (typeof value !== 'number' || isNaN(value)) return
    // 限制范围
    let adjustedValue = value
    if (value < MIN_TOTAL) adjustedValue = MIN_TOTAL
    if (value > MAX_TOTAL) adjustedValue = MAX_TOTAL

    // 回填到输入框
    form.setFieldsValue({ total: adjustedValue })

    // 匹配商品：quantity + freeQuantity
    const matched = commodities.find(c => c.quantity + (c.freeQuantity || 0) === adjustedValue)

    if (matched) {
      setCommodity(matched)
      setQuantity(1)
      form.setFieldsValue({ commodityId: matched.id })
    } else if (baseOneCommodity) {
      // 没匹配到 → 选 quantity === 1 的商品，价格 = 输入数量 × 单价
      setCommodity(baseOneCommodity)
      setQuantity(value)
      form.setFieldsValue({ commodityId: baseOneCommodity.id })
    } else {
      setCommodity(null)
      form.setFieldsValue({ commodityId: undefined })
    }
  }

  // 提交处理
  const handleRecharge = (values: FormValuesType) => {
    if (!commodity) return

    PopUpStore.setPayPopup({
      visible: true,
      commodity: {
        ...commodity,
        name: values.total + ' ' + t('unit.diamond', { count: values.total })
      },
      quantity,
      currency: commodity.paymentCurrencyCode,
      currencyPrefix: commodity.prefix,
      amount: commodity.price * quantity,
      createOrder: async data => {
        const { commodity, quantity, paymentCode } = data
        const res = await TGASDK.module.Wallet.service.createChargeOrder({
          commodityId: commodity.id,
          quantity,
          paymentCode: 'STRIPE_PAY'
        })
        return res.data
      }
    })
  }

  // 初始化默认值
  useEffect(() => {
    if (commodities && commodities.length > 0) {
      const def = commodities.find(item => item.isDefault) || commodities[0]
      handleSelect(def)
    }
  }, [commodities])

  if (!commodities) {
    return <Loading className="h-40" />
  }

  return (
    <Form form={form} className="flex flex-col gap-md" onFinish={handleRecharge}>
      {/* 隐藏字段 commodityId */}
      <Field<FormValuesType> name="commodityId">
        <Input hidden />
      </Field>

      <div>
        <h3 className="mb-sm">{t('title.diamondCharge')}</h3>
        <div className="grid grid-cols-3 gap-sm">
          {otherCommodities.map(item => {
            return (
              <DiamondCommodityItem
                key={item.id}
                item={item}
                actived={item.id === commodity?.id}
                onSelect={handleSelect}
              />
            )
          })}
        </div>
      </div>

      {/* 只有有数量为 1 的商品才显示 */}
      {baseOneCommodity && (
        <div>
          <h3 className="mb-sm">{t('title.diamondChargeCustom')}</h3>
          <Field<FormValuesType> name="total">
            <Input
              suffix={<div className="font-medium">{t('unit.diamond', { count: form.getFieldValue('total') })}</div>}
              placeholder={MIN_TOTAL + ' - ' + MAX_TOTAL}
              type="number"
              min={MIN_TOTAL}
              max={MAX_TOTAL}
              onChange={e => handleTotalChange(Number(e.target.value))}
            />
          </Field>
        </div>
      )}

      <div className="mt-md">
        <Button htmlType="submit" type="primary" className="w-full">
          {commodity && t('btn.chargeDiamond', { price: commodity.prefix + (commodity.price * quantity).toFixed(2) })}
        </Button>
      </div>
    </Form>
  )
}

interface PropType {}

interface FormValuesType {
  commodityId?: string
  // 数量
  total?: number
}

export default DiamondForm
